import { Color, ColorVector, TagBorderMode } from '@fe/core';
import { Position } from 'geojson';
import { IFlightItem } from '../flights/types';
import { IconSize } from '../IconSize';
import { IconType, SubstituteIconType } from '../IconType';
import { RangeType } from '../RangeType';
import { FlownRouteColorSettings } from '../routes/types';

export enum CoastMode {
    noCoast = 0,
    toTarget = 1,
    project = 2
}

export interface WindParticlesProps {
    isEnabled: boolean;
    colorLight: ColorVector;
    colorDark: ColorVector;
    currentColor: ColorVector;
    opacity?: number;
    layerName?: string | null;
    data: {
        data: Uint8Array;
        width: number;
        height: number;
    } | null;
}

export interface TagProps {
    color: ColorVector;
    leaderColor: ColorVector;
    background: ColorVector;
    outlineMode: TagBorderMode;
    outlineColor: ColorVector;
    autoTag: boolean;
    hideLeader: boolean;
    drawBackground: boolean;
    minZoom: number;
    hasFlightDetailsTag: boolean; // Decides if selected flights should show tags
}

export interface TootipProps {
    strong: {
        fontWeight: string;
    };
    margin: string;
    padding: string;
    color: Color;
    backgroundColor: Color;
    fontSize: string;
}

export interface RingProps {
    isVisible: boolean;
    range: number;
    type: RangeType;
    color: ColorVector;
    lineColor: ColorVector;
}

export interface VectorProps {
    isVisible: boolean;
    lineColor: ColorVector;
    range: number;
    type: RangeType;
}

export interface ActiveFlightProps {
    isCoastMode: boolean;
    coastDebugOverlay: boolean;
    showProjectedPoints: boolean;
    defaultFlightColor: ColorVector;
    historyColor: ColorVector;
    hiddenColor: ColorVector;
    highlightColor: ColorVector;
    showShadow: boolean;
    visible: boolean;
    historySmoothing: boolean;
    historyModern: boolean;
    historyUseAircraftColor: boolean;
    iconSize: IconSize;
    substituteIconType: SubstituteIconType;
    iconZoomThreshold: number; // if less flight icons are circles
    iconSizeZoomThreshold: number; // when icon changes size
    isMonitorStatus: boolean;
    showDiversions: boolean;
    diversionLineOldColor: ColorVector;
    diversionLineNewColor: ColorVector;
    showOnlySelected: boolean;
    tags: TagProps;
    tooltipStyle: TootipProps;
    rings: RingProps;
    vectors: VectorProps;
    followedFlightId: number | null;
    routeFlownEnabledFlights: number[];
}

export interface SimpleFlightProps {
    visible: boolean;
    color?: ColorVector;
}

export interface FlightsProps {
    visible: boolean;
    active: ActiveFlightProps;
    scheduled: SimpleFlightProps;
    planned: SimpleFlightProps;
    parked: SimpleFlightProps;
    groundVehicles: SimpleFlightProps;
}

export interface SmoothingProps {
    iteration: number;
    factor: number;
    dimension: number;
}

export interface AircraftTypeRecord {
    acType: string | null;
    speed: number | null;
    fuelBurningRate: number | null;
    fuelReserve: number | null;
}

export interface FlightForwardFlight {
    position: Position;
    index: number;
    altitude?: number | null;
    time: number | null;
    heading: number | null;
}

export type FlightForwardProps = { show: false } | { show: true; flightId: number; iconType: IconType };

export type CityPairSettings = {
    show: boolean;
    active: boolean;
    planned: boolean;
    scheduled: boolean;
    activeColor: string;
    plannedColor: string;
    scheduledColor: string;
};

export interface DeckOverlayProps {
    windParticles: WindParticlesProps;
    flights: FlightsProps;
    smoothingParams: SmoothingProps;
    // TODO: remove this form here and share settings with route flown plugin
    routeFlown: Pick<FlownRouteColorSettings, 'shouldColorize'> & { isVisible: boolean };
    globeView: {
        useGlobeView: boolean;
    };
    cityPair?: CityPairSettings;
    aircraftParameters: {
        defaultParameters: AircraftTypeRecord | null;
        aircraftTypeParameters: AircraftTypeRecord[] | null;
    };
}

export interface TagLeaderItem {
    offset: [number, number];
    flight: IFlightItem;
}

export interface DepartureQueryItem {
    items: any[];
    airport: string;
}

export interface DepartureQueryResult {
    scheduled: DepartureQueryItem[];
    planned: DepartureQueryItem[];
}
